/**
 * The strip above the desk that says whether anything is being checked at all.
 *
 * Off is the default, and it has to be impossible to miss: a desk full of green
 * rows under a switched-off engine is the most dangerous thing this page could
 * show, because the green is last week's. So off is a warning in words, with the
 * switch beside it for whoever may turn it on.
 *
 * On, the strip answers the two questions that decide whether the rows can be
 * trusted: did the last round run (and when), and will anybody notice if the
 * server itself dies. The second is the heartbeat - the outside service that
 * alarms when our pings stop - and "not configured" is said as a warning too.
 * Only whether one is set is ever known here: the address is a secret the
 * server keeps (`heartbeatConfigured`).
 *
 * The switch writes straight through, like the R9 watch's: a stop button with a
 * save step is not a stop button.
 */
import { Activity, AlertTriangle, HeartPulse, Settings2 } from "lucide-react";
import { Switch } from "@wa/ui";
import { beatNoteKey, engineHealth, type MonitorEngineStatus } from "../../../lib/monitoring";
import { Btn, fmtDateTime, fmtStamp } from "../../ui";
import type { T } from "./words";

export function EngineStrip({
  engine,
  canManage,
  saving,
  now,
  t,
  onToggle,
  onSettings,
}: {
  engine: MonitorEngineStatus;
  canManage: boolean;
  saving: boolean;
  now: number;
  t: T;
  onToggle: (enabled: boolean) => void;
  onSettings: () => void;
}) {
  const health = engineHealth(engine, now);
  const warn = health !== "ok" && health !== "waiting";
  const when = fmtStamp(engine.lastBeatAt);
  const noteKey = beatNoteKey(engine.lastBeatNote);

  const line =
    health === "off"
      ? t("monitoring.engine.off")
      : health === "waiting"
        ? t("monitoring.engine.waiting", { minutes: engine.intervalMinutes })
        : health === "late"
          ? t("monitoring.engine.late", { when })
          : health === "failing"
            ? noteKey
              ? t(`monitoring.engine.${noteKey}`, { when })
              : engine.lastBeatNote
                ? t("monitoring.engine.failingNote", { when, note: engine.lastBeatNote })
                : t("monitoring.engine.failing", { when })
            : t("monitoring.engine.on", { minutes: engine.intervalMinutes, when });

  return (
    <section className="crm-card mb-4">
      <div className="crm-card-head">
        <span
          className="crm-kpi-tile"
          style={
            warn
              ? {
                  background: "color-mix(in oklab, var(--color-warning) 16%, transparent)",
                  color: "color-mix(in oklab, var(--color-warning) 55%, var(--color-foreground))",
                }
              : {
                  background: "color-mix(in oklab, var(--color-primary) 13%, transparent)",
                  color: "var(--color-primary)",
                }
          }
        >
          {warn ? (
            <AlertTriangle className="size-[18px]" strokeWidth={1.7} />
          ) : (
            <Activity className="size-[18px]" strokeWidth={1.7} />
          )}
        </span>
        <div className="crm-card-col">
          <div className="crm-card-t">{t("monitoring.engine.title")}</div>
          <div className="crm-card-s" title={engine.lastBeatAt ? fmtDateTime(engine.lastBeatAt) : undefined}>
            {line}
          </div>
        </div>
        <div className="crm-card-act">
          {canManage ? (
            <>
              <Btn small onClick={onSettings}>
                <Settings2 className="size-[15px]" strokeWidth={1.8} />
                {t("monitoring.settings.open")}
              </Btn>
              <Switch
                checked={engine.enabled}
                disabled={saving}
                onCheckedChange={onToggle}
                aria-label={t("monitoring.engine.switch")}
                className="ml-1"
              />
            </>
          ) : null}
        </div>
      </div>

      {engine.enabled ? (
        <p className="crm-sub flex items-center gap-2 px-[26px] pb-4 text-[12.5px]">
          {engine.heartbeatConfigured ? (
            <HeartPulse className="size-4 shrink-0 text-[var(--color-success)]" strokeWidth={1.7} />
          ) : (
            <AlertTriangle className="size-4 shrink-0 text-[var(--color-warning)]" strokeWidth={1.7} />
          )}
          {engine.heartbeatConfigured ? t("monitoring.engine.heartbeatOn") : t("monitoring.engine.heartbeatOff")}
        </p>
      ) : null}
    </section>
  );
}
